<template>
  <div class="flex flex-col gap-6 proxima_nova">
    <div class="flex flex-row justify-between items-center">
      <span class="text-xl font-semibold text_color">Custom Events</span>
      <a-button
        class="primary_medium cursor-pointer"
        @click="handleCreateEvent"
      >
        Create Custom Event
      </a-button>
    </div>

    <div
      v-if="loading && customEvents.length === 0"
      style="height: 60vh"
      class="overflow-hidden"
    >
      <SkeletonLoader :page="loaderPages.NAV_LIST" />
    </div>

    <EmptyScreen
      v-else-if="!loading && customEvents.length === 0"
      source="custom_events"
      text="No custom events created yet!"
      description="Create a new custom event to get started"
      padding="my-[30vh]"
    >
      <a-button class="primary_medium mt-4" @click="handleCreateEvent">
        Create Custom Event
      </a-button>
    </EmptyScreen>

    <SimpleTable
      v-else
      :headers="customEventHeaders"
      :data="customEvents"
      :custom-column-render="customColumnRender"
      :show-actions-column="true"
      :actions="actionOptions"
      :height="'calc(100vh - (240px + var(--banner-height)))'"
      disable-horizontal-scroll
    />
  </div>

  <ConfirmationModal
    v-model:visible="isDeleteModalVisible"
    title="Delete Custom Event"
    :message="`Are you sure you want to delete '${selectedEvent?.name}'?`"
    :loading="isDeleting"
    type="delete"
    confirm-text="Delete"
    loading-text="Deleting..."
    @confirm="deleteCustomEvent"
    @cancel="isDeleteModalVisible = false; selectedEvent = null"
  />
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { message } from "ant-design-vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import SimpleTable from "@/src/components/custom_table_components/SimpleTable.vue";
import ConfirmationModal from "@/src/components/ConfirmationModal.vue";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import CustomEventService from "@/src/api/CustomEventService.js";
import CustomEventToggle from "@/src/views/custom_events/CustomEventToggle.vue";
import { customEventHeaders } from "@/src/constants/customEventsConstants.js";
import { formatTimestamp } from "@/src/utils/dateTimeUtils.js";

const router = useRouter();

const customEvents = ref([]);
const loading = ref(false);
const isDeleting = ref(false);
const isDeleteModalVisible = ref(false);
const selectedEvent = ref(null);

const customColumnRender = {
  status: {
    component: CustomEventToggle,
    props: (record) => ({ row: record }),
  },
};

const actionOptions = [
  {
    label: "Delete",
    handler: (row) => handleDeleteConfirm(row),
    icon: imageNames.DELETE_GREY,
  },
];

const fetchCustomEvents = async () => {
  try {
    loading.value = true;
    const response = await CustomEventService.getCustomEvents();
    if (response?.success && response?.custom_events?.length > 0) {
      customEvents.value = response.custom_events.map((event) => ({
        id: event.id,
        name: event.event_name,
        created_by: event.created_by_name,
        updated_at: formatTimestamp(event.updated_at, "tableDateTime"),
        status: event.status ?? false,
      }));
    } else {
      customEvents.value = [];
    }
  } catch (error) {
    console.error("Error fetching custom events:", error);
    message.error("Failed to fetch custom events");
  } finally {
    loading.value = false;
  }
};


const handleCreateEvent = () => {
  router.push({ name: "custom-event-create" });
};


const handleDeleteConfirm = (row) => {
  selectedEvent.value = row;
  isDeleteModalVisible.value = true;
};

const deleteCustomEvent = async () => {
  try {
    isDeleting.value = true;
    const response = await CustomEventService.deleteCustomEvent(
      selectedEvent.value.id,
    );
    if (response?.success) {
      message.success("Custom event deleted successfully");
      isDeleteModalVisible.value = false;
      selectedEvent.value = null;
      await fetchCustomEvents();
    } else if (response?.error) {
      message.error(response.error);
    } else {
      message.error("Failed to delete custom event");
    }
  } catch (error) {
    console.error("Error deleting custom event:", error);
    const errorMessage =
      error?.response?.data?.error || "Failed to delete custom event";
    message.error(errorMessage);
  } finally {
    isDeleting.value = false;
  }
};

onMounted(() => {
  fetchCustomEvents();
});
</script>